import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CollectionPreferences,
  ColumnLayout,
  ContentLayout,
  Flashbar,
  FlashbarProps,
  Header,
  Input,
  Modal,
  Multiselect,
  Pagination,
  Select,
  SpaceBetween,
  Table,
  FormField,
  Textarea,
  TextFilter,
} from '@cloudscape-design/components';
import useUserProfile from 'src/hooks/useUserProfile';
import { useDivision } from 'src/store/division/useDivision';
import { useUsers } from 'src/store/users/useUsers';
import {
  checkFeaturePermission,
  formatDateTime,
  DateFormats,
} from 'src/utils/generalUtils';
import permissionConfig from 'src/contstant/permissionAccessConfig';
import { Persona } from 'src/contstant/Persona';
import { useListReasonsQuery } from 'src/data/api/helios-service-ts/reason/queries/useListReasonsQuery';
import { useCreateReasonMutation } from 'src/data/api/helios-service-ts/reason/queries/useCreateReasonMutation';
import { useUpdateReasonMutation } from 'src/data/api/helios-service-ts/reason/queries/useUpdateReasonMutation';
import { useDeactivateReasonMutation } from 'src/data/api/helios-service-ts/reason/queries/useDeactivateReasonMutation';
import { ReasonCategory } from '@amzn/gbsa-helios-typescript-client';
import type { Reason } from '@amzn/gbsa-helios-typescript-client';

const ConfigurationPage = () => {
  const { getUserProgramId, getUserBusinessUnitId, getUserId, getPersona, getDivisions } =
    useUserProfile();
  const { divisionList, loadDivisionsByBusinessUnit } = useDivision();
  const { userPermission, checkUserPermissions, currentUser } = useUsers();

  // Permission gate: only users with EditDivision (or Platform Admins) may
  // create, edit, or delete Not Applicable Reason Codes. Read-only users can
  // still view the configured codes.
  const isReadOnly = useMemo(
    () =>
      !checkFeaturePermission(
        getPersona(),
        permissionConfig.DIVISION.EDIT_DIVISION,
        userPermission?.permissions,
      ),
    [userPermission, getPersona],
  );

  const [selectedDivisions, setSelectedDivisions] = useState<
    ReadonlyArray<{ label: string; value: string }>
  >([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newReasonLabel, setNewReasonLabel] = useState('');
  const [newReasonDivision, setNewReasonDivision] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [labelError, setLabelError] = useState('');
  const [divisionError, setDivisionError] = useState('');
  const [createApiError, setCreateApiError] = useState('');
  const [notifications, setNotifications] = useState<FlashbarProps.MessageDefinition[]>(
    [],
  );
  const [reasonToDelete, setReasonToDelete] = useState<Reason | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  const [searchText, setSearchText] = useState('');
  const [visibleColumns, setVisibleColumns] = useState([
    'label',
    'divisionName',
    'createdBy',
    'createdAt',
    'lastUpdatedBy',
    'lastUpdatedAt',
    'actions',
  ]);

  useEffect(() => {
    loadDivisionsByBusinessUnit({
      programId: getUserProgramId(),
      businessUnitId: getUserBusinessUnitId(),
      isDeleted: false,
    });
  }, []);

  // Load the user's feature permissions so the edit gate can be evaluated.
  // Platform Admins implicitly have access, so the lookup is skipped for them.
  useEffect(() => {
    if (getPersona() !== Persona.PLATFORM_ADMIN) {
      checkUserPermissions({
        userId: getUserId(),
        programId: getUserProgramId(),
        businessUnit: getUserBusinessUnitId(),
        permissions: [permissionConfig.DIVISION.EDIT_DIVISION],
        divisions: getDivisions(),
      });
    }
  }, [currentUser]);

  const businessUnitId = getUserBusinessUnitId();

  const {
    data: listData,
    isLoading,
    error: listError,
  } = useListReasonsQuery({
    businessUnitId,
    category: ReasonCategory.NA_REASON,
    activeOnly: true,
  });

  const reasons = listData?.reasons ?? [];

  const filteredReasons = useMemo(() => {
    let result = reasons;
    if (selectedDivisions.length > 0) {
      const selectedValues = new Set(selectedDivisions.map((d) => d.value));
      result = result.filter((r) => r.divisionName && selectedValues.has(r.divisionName));
    }
    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase();
      result = result.filter(
        (r) =>
          r.label?.toLowerCase().includes(query) ||
          r.divisionName?.toLowerCase().includes(query) ||
          r.createdBy?.toLowerCase().includes(query),
      );
    }
    return result;
  }, [reasons, selectedDivisions, searchText]);

  const createMutation = useCreateReasonMutation({
    onSuccess: () => {
      setShowCreateModal(false);
      // Auto-add the division to the filter so the new reason is visible
      if (newReasonDivision && selectedDivisions.length > 0) {
        setSelectedDivisions((prev) => {
          const alreadySelected = prev.some((d) => d.value === newReasonDivision.value);
          if (!alreadySelected) {
            return [...prev, newReasonDivision];
          }
          return prev;
        });
      }
      setNewReasonLabel('');
      setNewReasonDivision(null);
      setLabelError('');
      setDivisionError('');
      setCreateApiError('');
    },
    onError: (error) => {
      setCreateApiError(error?.message || 'Failed to create reason.');
    },
  });

  const updateMutation = useUpdateReasonMutation({
    onError: (error) => {
      setNotifications([
        {
          type: 'error',
          dismissible: true,
          onDismiss: () => setNotifications([]),
          content: error?.message || 'Failed to update reason.',
          id: 'update-reason-error',
        },
      ]);
    },
  });

  const deactivateMutation = useDeactivateReasonMutation({
    onSuccess: () => {
      setReasonToDelete(null);
    },
    onError: (error) => {
      setReasonToDelete(null);
      setNotifications([
        {
          type: 'error',
          dismissible: true,
          onDismiss: () => setNotifications([]),
          content: error?.message || 'Failed to deactivate reason.',
          id: 'deactivate-reason-error',
        },
      ]);
    },
  });

  // Show list fetch errors via notification
  useEffect(() => {
    if (listError) {
      setNotifications([
        {
          type: 'error',
          dismissible: true,
          onDismiss: () => setNotifications([]),
          content: listError.message || 'Failed to load reason codes.',
          id: 'load-reasons-error',
        },
      ]);
    }
  }, [listError]);

  const handleCreateReason = () => {
    if (isReadOnly) return;
    let hasError = false;
    setLabelError('');
    setDivisionError('');
    setCreateApiError('');

    if (!newReasonLabel.trim()) {
      setLabelError('Reason label is required.');
      hasError = true;
    }
    if (!newReasonDivision) {
      setDivisionError('Division is required.');
      hasError = true;
    }
    if (hasError) return;

    createMutation.mutate({
      businessUnitId,
      category: ReasonCategory.NA_REASON,
      divisionName: newReasonDivision!.value,
      label: newReasonLabel.trim(),
      createdBy: getUserId(),
    });
  };

  const handleDeactivateReason = (reason: Reason) => {
    if (isReadOnly) return;
    setReasonToDelete(reason);
  };

  const confirmDeactivateReason = () => {
    if (isReadOnly) return;
    if (!reasonToDelete) return;
    deactivateMutation.mutate({
      businessUnitId,
      reasonId: reasonToDelete.reasonId,
      category: ReasonCategory.NA_REASON,
      divisionName: reasonToDelete.divisionName,
      deactivatedBy: getUserId(),
    });
  };

  const divisionOptions = divisionList.map((division) => ({
    label: division.divisionName,
    value: division.divisionName,
  }));

  return (
    <ContentLayout
      data-testid="configuration-content-layout"
      header={
        <Header data-testid="configuration-header" variant="h1">
          Division Configuration
        </Header>
      }
    >
      <SpaceBetween size="l">
        {notifications.length > 0 && <Flashbar items={notifications} />}
        <ColumnLayout columns={4}>
          <FormField>
            <Multiselect
              selectedOptions={selectedDivisions}
              onChange={({ detail }) => {
                setSelectedDivisions(
                  detail.selectedOptions as ReadonlyArray<{
                    label: string;
                    value: string;
                  }>,
                );
                setCurrentPage(1);
              }}
              options={divisionOptions}
              placeholder="Filter by division"
              filteringType="auto"
              filteringPlaceholder="Search divisions"
            />
          </FormField>
        </ColumnLayout>
        <Table<Reason>
          items={filteredReasons.slice(
            (currentPage - 1) * PAGE_SIZE,
            currentPage * PAGE_SIZE,
          )}
          loading={isLoading}
          loadingText="Loading reasons..."
          stripedRows
          wrapLines
          filter={
            <TextFilter
              filteringText={searchText}
              filteringPlaceholder="Search reasons"
              filteringAriaLabel="Search reasons"
              onChange={({ detail }) => {
                setSearchText(detail.filteringText);
                setCurrentPage(1);
              }}
            />
          }
          pagination={
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={Math.ceil(filteredReasons.length / PAGE_SIZE) || 1}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
            />
          }
          preferences={
            <CollectionPreferences
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={{ visibleContent: visibleColumns }}
              onConfirm={({ detail }) =>
                setVisibleColumns([...(detail.visibleContent ?? visibleColumns)])
              }
              visibleContentPreference={{
                title: 'Visible columns',
                options: [
                  {
                    label: 'Table columns',
                    options: [
                      { id: 'label', label: 'Reason', editable: false },
                      { id: 'divisionName', label: 'Division' },
                      { id: 'createdBy', label: 'Created By' },
                      { id: 'createdAt', label: 'Created At' },
                      { id: 'lastUpdatedBy', label: 'Updated By' },
                      { id: 'lastUpdatedAt', label: 'Updated At' },
                      { id: 'actions', label: 'Delete' },
                    ],
                  },
                ],
              }}
            />
          }
          visibleColumns={visibleColumns}
          submitEdit={async (item, column, newValue) => {
            if (isReadOnly) return;
            if (column.id === 'label' && newValue && newValue !== item.label) {
              await updateMutation.mutateAsync({
                businessUnitId,
                reasonId: item.reasonId,
                category: ReasonCategory.NA_REASON,
                divisionName: item.divisionName,
                label: (newValue as string).trim(),
                lastUpdatedBy: getUserId(),
              });
            }
          }}
          header={
            <Header
              variant="h3"
              actions={
                !isReadOnly && (
                  <Button
                    variant="normal"
                    onClick={() => {
                      setLabelError('');
                      setDivisionError('');
                      setCreateApiError('');
                      setNewReasonLabel('');
                      setNewReasonDivision(null);
                      setShowCreateModal(true);
                    }}
                  >
                    Create Reason Code
                  </Button>
                )
              }
            >
              Not Applicable Reason Codes
            </Header>
          }
          empty={
            <Box textAlign="center" color="text-body-secondary" padding="l">
              No Not Applicable reason codes found.
            </Box>
          }
          columnDefinitions={[
            {
              id: 'label',
              header: 'Reason',
              cell: (item) => item.label,
              width: '35%',
              ...(isReadOnly
                ? {}
                : {
                    editConfig: {
                      editingCell: (item, { currentValue, setValue }) => (
                        <Textarea
                          autoFocus
                          value={currentValue ?? item.label}
                          onChange={({ detail }) => setValue(detail.value)}
                          rows={3}
                        />
                      ),
                    },
                  }),
            },
            {
              id: 'divisionName',
              header: 'Division',
              cell: (item) => item.divisionName ?? '-',
            },
            {
              id: 'createdBy',
              header: 'Created By',
              cell: (item) => item.createdBy,
            },
            {
              id: 'createdAt',
              header: 'Created At',
              cell: (item) =>
                item.createdAt
                  ? formatDateTime(item.createdAt, DateFormats.LONG_DATE)
                  : '-',
            },
            {
              id: 'lastUpdatedBy',
              header: 'Updated By',
              cell: (item) => item.lastUpdatedBy ?? '-',
            },
            {
              id: 'lastUpdatedAt',
              header: 'Updated At',
              cell: (item) =>
                item.lastUpdatedAt
                  ? formatDateTime(item.lastUpdatedAt, DateFormats.LONG_DATE)
                  : '-',
            },
            {
              id: 'actions',
              header: 'Delete',
              cell: (item) =>
                isReadOnly ? (
                  '-'
                ) : (
                  <Button
                    variant="inline-icon"
                    iconName="remove"
                    ariaLabel="Deactivate reason"
                    onClick={() => handleDeactivateReason(item)}
                  />
                ),
              width: 80,
            },
          ]}
        />
      </SpaceBetween>

      <Modal
        visible={showCreateModal}
        onDismiss={() => setShowCreateModal(false)}
        header="Create Not Applicable Reason Code"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateReason}
                loading={createMutation.isPending}
              >
                Create
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {createApiError && (
            <Flashbar
              items={[
                {
                  type: 'error',
                  dismissible: true,
                  onDismiss: () => setCreateApiError(''),
                  content: createApiError,
                  id: 'create-reason-api-error',
                },
              ]}
            />
          )}
          <FormField
            label="Reason Label"
            errorText={labelError}
            description="Enter the Not Applicable reason code label (e.g., 'Equipment unavailable')"
          >
            <Input
              value={newReasonLabel}
              onChange={({ detail }) => setNewReasonLabel(detail.value)}
              placeholder="Enter reason label"
            />
          </FormField>
          <FormField label="Division" errorText={divisionError}>
            <Select
              selectedOption={newReasonDivision}
              onChange={({ detail }) =>
                setNewReasonDivision(
                  detail.selectedOption as { label: string; value: string },
                )
              }
              options={divisionOptions}
              placeholder="Select a division"
              filteringType="auto"
              filteringPlaceholder="Search divisions"
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={reasonToDelete !== null}
        onDismiss={() => setReasonToDelete(null)}
        header="Delete reason code"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setReasonToDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmDeactivateReason}
                loading={deactivateMutation.isPending}
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Are you sure you want to delete the reason code &quot;{reasonToDelete?.label}
        &quot;? This action cannot be undone.
      </Modal>
    </ContentLayout>
  );
};

export default ConfigurationPage;
